import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Activity, Profile, RolNivel } from '@/types'

// Bulk-fetches everything the manager Tours grid needs to render N rich cards
// without triggering N×2 round-trips. Two queries total, then fanned out to a
// per-tour map in memory. RLS ensures the manager only sees their org's rows.
//
// Activities query is a column-narrow read — we only need the fields used in
// the date strip / next-up / day-type derivation.

export interface TourCrewMember {
  user_id: string
  rol_nivel: RolNivel
  profile: Profile | null
}

export interface TourStats {
  activities: Activity[]
  crew: TourCrewMember[]
}

export function useOrgTourStats(orgId: string | undefined, tourIds: string[]) {
  const idsKey = tourIds.slice().sort().join(',')

  return useQuery({
    queryKey: ['org-tour-stats', orgId, idsKey],
    enabled: !!orgId && tourIds.length > 0,
    queryFn: async (): Promise<Map<string, TourStats>> => {
      const [actsRes, crewRes] = await Promise.all([
        supabase
          .from('activities')
          .select('id, tour_id, titulo, descripcion, venue_name, venue_timezone, start_time_utc, end_time_utc, visibility_level, orden, created_at, updated_at')
          .in('tour_id', tourIds),
        supabase
          .from('tour_memberships')
          .select('tour_id, user_id, rol_nivel')
          .in('tour_id', tourIds),
      ])
      if (actsRes.error) throw actsRes.error
      if (crewRes.error) throw crewRes.error

      const activities = (actsRes.data ?? []) as (Activity & { tour_id: string })[]
      const memberships = (crewRes.data ?? []) as { tour_id: string; user_id: string; rol_nivel: RolNivel }[]

      const userIds = Array.from(new Set(memberships.map(m => m.user_id)))
      let profilesById = new Map<string, Profile>()
      if (userIds.length > 0) {
        const { data: profiles, error: pErr } = await supabase
          .from('profiles')
          .select('*')
          .in('id', userIds)
        if (pErr) throw pErr
        profilesById = new Map((profiles ?? []).map(p => [p.id as string, p as Profile]))
      }

      const map = new Map<string, TourStats>()
      for (const id of tourIds) map.set(id, { activities: [], crew: [] })

      for (const a of activities) {
        const slot = map.get(a.tour_id)
        if (slot) slot.activities.push(a)
      }
      for (const m of memberships) {
        const slot = map.get(m.tour_id)
        if (slot) {
          slot.crew.push({
            user_id: m.user_id,
            rol_nivel: m.rol_nivel,
            profile: profilesById.get(m.user_id) ?? null,
          })
        }
      }

      return map
    },
    staleTime: 30_000,
  })
}
